import React from 'react';

/**
 * A placeholder shaped like the thing that is coming.
 *
 * The app said "Loading…" in the middle of an empty page. That is honest but
 * it gives the eye nothing to settle on, and it reads as slower than it is —
 * a blank page with a word on it feels stalled, while an outline of the layout
 * feels like it is arriving. It matters more since the heavy routes became
 * separate chunks: the first visit to the pipeline now fetches 220K before it
 * can render anything.
 *
 * Deliberately plain. A skeleton that is more interesting than the content is
 * a skeleton someone will notice twice.
 */
export default function Skeleton({ variant = 'page', label = 'Loading' }) {
  // The label is for screen readers, which get nothing at all from a shimmer.
  const a11y = { role: 'status', 'aria-live': 'polite', 'aria-label': `${label}…` };

  if (variant === 'list') {
    return (
      <div className="skeleton-wrap" {...a11y}>
        {[0, 1, 2, 3, 4].map(i => (
          <div key={i} className="skeleton-row">
            <div className="skeleton-block skeleton-avatar" />
            <div className="skeleton-lines">
              <div className="skeleton-block skeleton-line wide" />
              <div className="skeleton-block skeleton-line" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (variant === 'canvas') {
    // Roughly where nodes sit, so the shape of what is coming is recognisable.
    return (
      <div className="skeleton-wrap skeleton-canvas" {...a11y}>
        <div className="skeleton-block skeleton-node" style={{ top: '18%', left: '8%' }} />
        <div className="skeleton-block skeleton-node" style={{ top: '18%', left: '38%' }} />
        <div className="skeleton-block skeleton-node" style={{ top: '48%', left: '38%' }} />
        <div className="skeleton-block skeleton-node" style={{ top: '18%', left: '68%' }} />
        <div className="skeleton-block skeleton-node" style={{ top: '48%', left: '68%' }} />
      </div>
    );
  }

  return (
    <div className="skeleton-wrap" {...a11y}>
      <div className="skeleton-block skeleton-title" />
      <div className="skeleton-block skeleton-line wide" />
      <div className="skeleton-block skeleton-line" />
      <div className="skeleton-block skeleton-line wide" />
      <div className="skeleton-block skeleton-line short" />
    </div>
  );
}
